import { Accordion, Badge, Group, Stack, Text, Timeline } from '@mantine/core';
import { useTranslation } from 'react-i18next';

import {
  TravelApprovalStep,
  TravelStepDecision,
  currentTravelApprovalStep,
  sortTravelApprovalSteps,
  travelStepFamily,
  travelStepKeyList,
  travelStepRoleLabel,
  travelStepTitle,
} from '@/module/Travel/Request/model/TravelRequestTypes';

import { formatDateTime } from '@feature/utils';

import classes from './TravelApprovalTimeline.module.css';

interface TravelApprovalTimelineProps {
  steps: TravelApprovalStep[];
}

interface RoundTimelineProps {
  steps: TravelApprovalStep[];
  currentId: number | null;
}

interface LineProps {
  label: string;
  value: string;
}

const decisionColor: Record<TravelStepDecision, string> = {
  PENDING: 'blue',
  APPROVED: 'green',
  RETURNED: 'yellow',
  REJECTED: 'red',
  SKIPPED: 'gray',
};

// A resubmission appends a fresh chain, so a round starts wherever the step keys stop climbing.
export function splitTravelApprovalRounds(steps: TravelApprovalStep[]) {
  const rounds: TravelApprovalStep[][] = [];
  let previousIndex = -1;
  sortTravelApprovalSteps(steps).forEach(step => {
    const index = travelStepKeyList.indexOf(step.stepKey);
    if (rounds.length === 0 || index <= previousIndex) {
      rounds.push([]);
    }
    rounds[rounds.length - 1].push(step);
    previousIndex = index;
  });
  return rounds;
}

function Line({ label, value }: LineProps) {
  return (
    <Group gap="xs">
      <Text
        size="xs"
        c="dimmed"
      >
        {label}
      </Text>
      <Text size="xs">{value}</Text>
    </Group>
  );
}

function RoundTimeline({ steps, currentId }: RoundTimelineProps) {
  const { t } = useTranslation();

  const approverLabel = (step: TravelApprovalStep) => {
    const role = travelStepRoleLabel(step, t);
    if (step.approver == null) {
      // The verification step's title already names its office.
      const roleNamed = step.stepKey === 'SECRETARIAT_VERIFICATION';
      return step.decision === 'PENDING' && !roleNamed ? role : null;
    }
    return [step.approver.name, role && `(${role})`].filter(Boolean).join(' ');
  };

  const decisionLabel = (step: TravelApprovalStep) => {
    if (step.decision === 'APPROVED') {
      return t(`travel.stepFamily.${travelStepFamily(step)}.positive`);
    }
    if (step.decision === 'REJECTED') {
      return t(`travel.stepFamily.${travelStepFamily(step)}.negative`);
    }
    return t(`travel.stepDecisionEnum.${step.decision}`);
  };

  const decidedAtLabel = (step: TravelApprovalStep) =>
    step.decision === 'APPROVED'
      ? t(`travel.stepFamily.${travelStepFamily(step)}.decidedAt`)
      : t('travel.requests.timeline.decidedAt');

  return (
    <Timeline
      role="list"
      bulletSize={18}
      lineWidth={2}
      classNames={{ item: classes.item }}
    >
      {steps.map(step => {
        const current = step.id === currentId;
        const approver = step.decision === 'SKIPPED' ? null : approverLabel(step);
        const stepLabel = travelStepTitle(step, t);

        return (
          <Timeline.Item
            key={step.id}
            role="listitem"
            aria-label={stepLabel}
            color={decisionColor[step.decision]}
            title={
              <Group gap="xs">
                <Text
                  size="sm"
                  fw="500"
                >
                  {stepLabel}
                </Text>
                <Badge
                  size="sm"
                  variant="light"
                  color={decisionColor[step.decision]}
                >
                  {decisionLabel(step)}
                </Badge>
              </Group>
            }
          >
            <Stack gap={2}>
              {approver && (
                <Line
                  label={t('travel.requests.timeline.approver')}
                  value={approver}
                />
              )}
              {step.reason && (
                <Line
                  label={t('travel.requests.timeline.reason')}
                  value={step.reason}
                />
              )}
              {step.decidedAt && (
                <Line
                  label={decidedAtLabel(step)}
                  value={formatDateTime(step.decidedAt)}
                />
              )}
              {step.decision === 'RETURNED' && step.restartedAfterChanges && (
                <Text
                  size="xs"
                  c="dimmed"
                >
                  {t('travel.requests.timeline.restartedAfterChanges')}
                </Text>
              )}
              {current && step.activatedAt && (
                <Line
                  label={t('travel.requests.timeline.pendingSince')}
                  value={formatDateTime(step.activatedAt)}
                />
              )}
              {step.sentToBoardAt && (
                <Line
                  label={t('travel.requests.timeline.sentToBoardAt')}
                  value={[formatDateTime(step.sentToBoardAt), step.sentToBoardByName && `(${step.sentToBoardByName})`]
                    .filter(Boolean)
                    .join(' ')}
                />
              )}
              {current && step.remindersSent > 0 && step.lastReminderAt && (
                <Line
                  label={t('travel.requests.timeline.reminderSentAt')}
                  value={formatDateTime(step.lastReminderAt)}
                />
              )}
            </Stack>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}

export function TravelApprovalTimeline({ steps }: TravelApprovalTimelineProps) {
  const { t } = useTranslation();

  const rounds = splitTravelApprovalRounds(steps);
  const currentRound = rounds[rounds.length - 1] ?? [];
  const previousRounds = rounds.slice(0, -1);
  const currentId = currentTravelApprovalStep(steps)?.id ?? null;

  return (
    <Stack gap="md">
      <RoundTimeline
        steps={currentRound}
        currentId={currentId}
      />
      {previousRounds.length > 0 && (
        <Accordion
          variant="contained"
          styles={{ control: { color: 'var(--mantine-color-text)' } }}
        >
          <Accordion.Item value="previous">
            <Accordion.Control>
              <Text
                size="sm"
                fw={500}
              >
                {t('travel.requests.timeline.previousRounds', { count: previousRounds.length })}
              </Text>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="lg">
                {previousRounds.map(round => (
                  <RoundTimeline
                    key={round[0].id}
                    steps={round}
                    currentId={null}
                  />
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      )}
    </Stack>
  );
}
